import { useState, useEffect } from "react";
import useRequest from "./useRequest";

/**
 * Custom hook to manage research prompt history (Admin side)
 * Loads client's research history from backend
 */
export const useResearchHistory = (clientId) => {
    const [researchHistories, setResearchHistories] = useState([]);
    const [loading, setLoading] = useState(false);
    const { request } = useRequest();

    // Load research histories from backend on mount or when clientId changes
    useEffect(() => {
        if (clientId) {
            loadResearchHistories();
        } else {
            setResearchHistories([]);
        }
    }, [clientId]);

    const loadResearchHistories = () => {
        if (!clientId) {
            setResearchHistories([]);
            return;
        }

        setLoading(true);
        // Admin route
        request({
            url: `/admin/access-module/research/history/${clientId}`,
            method: "GET",
            onSuccess: (data) => {
                const histories = data?.data?.histories || [];
                // Map backend format to frontend format
                const mappedHistories = histories.map((h) => ({
                    id: h._id || h.id,
                    title: h.title || "New Research",
                    formData: h.formData || {},
                    researchResults: h.researchResults || {},
                    createdAt: h.created_at || h.createdAt,
                    updatedAt: h.updated_at || h.updatedAt,
                }));
                setResearchHistories(mappedHistories);
                setLoading(false);
            },
            onError: (err) => {
                console.error("Error loading research histories:", err);
                setResearchHistories([]);
                setLoading(false);
            },
        });
    };

    // Save research history for the client
    const saveResearchHistory = (formData, researchResults = {}) => {
        if (!clientId) {
            return Promise.reject(new Error("Client ID is required"));
        }

        // Create title from topic (truncate to 40 chars)
        let title = "New Research";
        if (formData.topic && formData.topic.trim()) {
            title = formData.topic.trim().substring(0, 40);
            if (formData.topic.length > 40) title += "...";
        }

        return new Promise((resolve, reject) => {
            // Admin route
            request({
                url: `/admin/access-module/research/history/${clientId}`,
                method: "POST",
                data: {
                    title,
                    formData,
                    researchResults,
                },
                onSuccess: (data) => {
                    const savedHistory = data?.data?.history;
                    if (savedHistory) {
                        const mappedHistory = {
                            id: savedHistory._id || savedHistory.id,
                            title: savedHistory.title,
                            formData: savedHistory.formData || {},
                            researchResults: savedHistory.researchResults || {},
                            createdAt: savedHistory.created_at || savedHistory.createdAt,
                            updatedAt: savedHistory.updated_at || savedHistory.updatedAt,
                        };
                        // Add to beginning of array
                        setResearchHistories((prev) => [mappedHistory, ...prev]);
                        resolve(mappedHistory);
                    } else {
                        reject(new Error("No history returned from server"));
                    }
                },
                onError: (err) => {
                    console.error("Error saving research history:", err);
                    reject(err);
                },
            });
        });
    };

    // Delete client history
    const deleteResearchHistory = (id) => {
        if (!clientId) {
            return Promise.reject(new Error("Client ID is required"));
        }

        return new Promise((resolve, reject) => {
            // Admin route
            request({
                url: `/admin/access-module/research/history/${clientId}/${id}`,
                method: "DELETE",
                onSuccess: () => {
                    setResearchHistories((prev) => prev.filter((h) => h.id !== id));
                    resolve(true);
                },
                onError: (err) => {
                    console.error("Error deleting client research history:", err);
                    reject(err);
                },
            });
        });
    };

    const clearAllResearchHistories = () => {
        if (!clientId) {
            return Promise.reject(new Error("Client ID is required"));
        }

        return new Promise((resolve, reject) => {
            // Admin route
            request({
                url: `/admin/access-module/research/history/${clientId}/clear`,
                method: "PUT",
                onSuccess: () => {
                    setResearchHistories([]);
                    resolve(true);
                },
                onError: (err) => {
                    console.error("Error clearing client research history:", err);
                    reject(err);
                },
            });
        });
    };

    const getResearchHistory = (id) => {
        return researchHistories.find((history) => history.id === id);
    };

    return {
        researchHistories,
        loading,
        saveResearchHistory,
        deleteResearchHistory,
        clearAllResearchHistories,
        getResearchHistory,
        loadResearchHistories,
    };
};
